import React, { useState, useCallback, useMemo, useRef, useEffect } from "react"
import { Box, Flex, useDisclosure, Text, Center } from "@chakra-ui/react"

import { useLocationContext } from "@app/providers/location"
import { useAppContext } from "@app/providers/app"
import { useShopifyProductLive, useShopifyProductRaw, useShopifyVariants } from "@app/hooks/useShopify"
import { useWorlds } from "@app/hooks/useWorlds"
import { useProductBadges } from "@app/hooks/useProductBadges"
import { useShopify } from "@app/hooks/useShopify"
import { useBundle } from "@app/hooks/useBundle"
import { ProductStock } from "@app/components/Product/Stock/ProductStock"
import ProductAddToCart from "@app/components/Product/ProductAddToCart"
import ProductAddBundleToCart from "@app/components/Product/ProductAddBundleToCart"
import ProductAddToCartBottom from "@app/components/Product/ProductAddToCartBottom"
import ProductDescriptionDesktop from "@app/components/Product/ProductDescriptionDesktop"
import ProductMedia from "@app/components/Product/ProductMedia"
import ProductTitle from "@app/components/Product/ProductTitle"
import ProductFloatingAddToCart from "@app/components/Product/ProductFloatingAddToCart"
import ProductQuantitySelector from "@app/components/Product/ProductQuantitySelector"
import ProductSiblingSelector from "@app/components/Product/ProductSiblingSelector"
import ProductWishlist from "@app/components/Product/ProductWishlist"
import ProductBackInStock from "@app/components/Product/ProductBackInStock"
import Discount from "@app/components/Discount"

import ScavengerHunt from "@app/components/ScavengerHunt/ScavengerHunt"
import ProductTabs from "@app/components/Product/ProductTabs/ProductTabs"
import ResolvedImage from "@app/components/ResolvedImage"

import type { Props } from "@app/templates/product"
import type { PageProps } from "@root/types/global"
import { useCore } from "@app/hooks/useCore"
import { useProductComputed } from "@app/hooks/useProductComputed"
import { useMedia } from "@app/hooks/useMedia"
import { useInsideDataProduct } from "@app/hooks/useInsideData"
import { useFunctions } from "@app/hooks/useFunctions"
import { ReviewDataResponseType } from "@root/types/reviews"
import { ProductReviewsPreview } from "@app/components/Product/ProductReviews/ProductReviewsPreview"

export type selectedOptions = Array<{
  name: string
  value: string
}>

const ProductBottom = React.lazy(() => import("@app/components/Product/ProductBottom"))

const ProductMain: React.FC<PageProps<Props>> = ({ product: sanityProduct, template }) => {
  const { locating } = useLocationContext()
  const { reviewsCache, setReviewsCache } = useAppContext()

  const {
    helpers: { decodeShopifyId },
  } = useCore()

  const { callFunction } = useFunctions()

  const { product: rawProduct } = useShopifyProductRaw(sanityProduct)
  const { product: liveProduct, loading, isUnavailable } = useShopifyProductLive(rawProduct)
  const product = liveProduct || rawProduct
  const [quantity, setQuantity] = useState(1)
  const { isOpen: backInStockisOpen, onClose: backInStockonClose, onOpen: handleBackInStock } = useDisclosure()
  const isLoading = loading || locating
  const [activeSibling, setActiveSibling] = useState(null)
  const { getProductWorld } = useWorlds()
  const { getBadgesByTags } = useProductBadges()
  const itemTags = useMemo(() => product?.tags?.filter((tag: string) => tag.startsWith("badge:")), [product?.tags])
  const badges = useMemo(() => getBadgesByTags(itemTags), [getBadgesByTags, itemTags])
  const { productSizeText } = useProductComputed(product)
  const { isBase, isMedium, isHydrated } = useMedia()
  const [reviewData, setReviewData] = useState<ReviewDataResponseType | null>(reviewsCache?.[product?.handle as string] || null)

  const productAddBottomRef = useRef<HTMLDivElement>(null)

  const { activeVariant } = useShopifyVariants({
    // Always select first available variant
    firstAvailable: true,
    loading: isLoading,
    product,
    useParameter: true,
  })

  useInsideDataProduct(product, activeVariant)

  const [noActiveOption, disableNoActiveOption] = useState(false)

  const changeQuantity = (newQty: number) => {
    if (newQty !== quantity && newQty > 0) setQuantity(newQty)
  }

  const siblings = useMemo(
    () =>
      (product?.siblings ? product.siblings : [])
        ?.filter((sibling: any) => sibling?.shopify?.active && sibling?.shopify?.published && !sibling?.shopify?.deleted)
        ?.map((sibling: any) => {
          const world = getProductWorld(sibling)
          return {
            ...sibling,
            world,
            colorTitle: sibling?.tags?.find((tag: string) => tag?.startsWith("colour:"))?.split(":")?.[1] || world?.name,
          }
        })
        ?.sort((a: any, b: any) => {
          if (a?.world?.order < b?.world?.order) return -1
          else if (a?.world?.order > b?.world?.order) return 1
          return 0
        }),
    [getProductWorld, product]
  )

  const resetActiveSibling = useCallback(() => {
    setActiveSibling(siblings?.find((sibling: any) => sibling?.shopify?.handle === product?.handle))
  }, [product?.handle, siblings])

  const hasVariantsDropdown = product?.variants?.length > 1

  const { imageNormaliser } = useShopify()
  const initialImages = useMemo(
    () => product?.images?.map((image: any) => imageNormaliser(image, 1000)) || [],
    [imageNormaliser, product?.images]
  )

  // Product Reviews

  const reviewsRef = useRef<HTMLDivElement>(null)

  const [isLoadingReviews, setIsLoadingReviews] = useState<boolean>(false)
  const mapReviewData = (response: any) => {
    return response
      ? {
          averageScore: response.bottomline?.average_score,
          totalReviews: response.bottomline?.total_review,
          reviews: response.reviews.map(review => ({
            author: review.user.display_name,
            contentMessage: review.content,
            contentTitle: review.title,
            createdDate: review.created_at,
            score: review.score,
            id: review.id,
            isCurated: review.is_incentivized,
          })),
        }
      : null
  }

  const getReviewsFunction = () => {
    if (!product?.id || isLoadingReviews) return
    setIsLoadingReviews(true)
    const fetchReviews = async () => {
      const {
        body: { response: reviewsResponse },
      } = await callFunction("get-reviews", { productId: decodeShopifyId(product.id, "Product") })

      const mappedReviewsResponse = mapReviewData(reviewsResponse) as ReviewDataResponseType
      setReviewData(mappedReviewsResponse)
      setReviewsCache({ ...reviewsCache, [product?.handle as string]: mappedReviewsResponse })
      setIsLoadingReviews(false)
    }
    fetchReviews()
  }

  useEffect(() => {
    const variantsSkus = product?.variants?.map(variant => variant?.sku)
    if (template?.settingEnableReviews && variantsSkus?.includes(activeVariant?.sku)) {
      getReviewsFunction()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant?.sku])

  const { isBundle } = useBundle(product)

  return (
    <>
      <ProductBackInStock onClose={backInStockonClose} isOpen={backInStockisOpen} product={product} />
      <Box pos="relative" zIndex={400} py={{ base: 0, md: "32px" }}>
        {/* PDP MEDIA AND FORM */}
        <Flex flexDir={{ base: "column-reverse", lg: "row" }}>
          <Flex w={{ base: "100%", lg: "50%" }} justify="center" alignItems={"center"} flex={1}>
            <Box mt={{ base: "24px", lg: "0" }} px="24px" maxW={{ base: "unset", lg: "600px" }} w="100%" position="relative">
              <Discount tags={product.tags} fontSize={16} />

              {reviewData && template?.settingEnableReviews ? (
                <ProductReviewsPreview
                  totalReviews={reviewData?.totalReviews || 0}
                  averageReview={reviewData?.averageScore || 0}
                  mb={2}
                  reviewsRef={reviewsRef}
                />
              ) : null}

              <Box justifyContent="space-between" mt={{ base: "10px", md: "0" }}>
                <ProductTitle product={product} activeVariant={activeVariant} />
              </Box>
              {productSizeText && (
                <Text variant="text16" lineHeight="24px" my={3}>
                  {productSizeText}
                </Text>
              )}
              <ProductDescriptionDesktop product={product} />
              <Flex flexDir="column" gap="24px">
                <ProductSiblingSelector
                  siblings={siblings}
                  activeSibling={activeSibling}
                  resetActiveSibling={resetActiveSibling}
                  setActiveSibling={setActiveSibling}
                  w={hasVariantsDropdown ? "100%" : "unset"}
                  mb={hasVariantsDropdown ? "20px" : 0}
                  zIndex={700}
                />
                {product?.fragrences?.featuresNew && product.fragrences.featuresNew.length > 0 && (
                  <Flex flexDir="row" gap="8px" justify="space-between" flexWrap="wrap">
                    {product.fragrences.featuresNew.map((feature: any) => (
                      <Center flexDir={{ base: "row", lg: "column" }} gap={{ base: "4px", lg: "0" }} key={feature._id}>
                        <ResolvedImage image={feature.iconImage} w="48px" h="48px" loading="eager" />
                        <Text textAlign={{ base: "left", lg: "center" }} maxW="84px" whiteSpace="break-spaces" lineHeight={1.5}>
                          {feature.name}
                        </Text>
                      </Center>
                    ))}
                  </Flex>
                )}
                <Flex flexDirection={{ base: "column", md: "row" }} gap={{ base: 6, md: 3 }} flex={1}>
                  <Flex gap={4} width={"100%"}>
                    <ProductQuantitySelector
                      product={product}
                      quantity={quantity}
                      changeQuantity={changeQuantity}
                      width={{ base: "100%", md: "auto" }}
                    />
                    {isBase ? null : isBundle ? (
                      <ProductAddBundleToCart product={product} quantity={quantity} loading={isLoading} />
                    ) : (
                      <ProductAddToCart
                        product={product}
                        quantity={quantity}
                        loading={isLoading}
                        handleBackInStock={handleBackInStock}
                        isUnavailable={isUnavailable}
                        selectedVariant={activeVariant}
                      />
                    )}
                    <ProductWishlist product={product} />
                  </Flex>
                  {isBase ? (
                    <Flex>
                      {isBundle ? (
                        <ProductAddBundleToCart product={product} quantity={quantity} loading={isLoading} />
                      ) : (
                        <ProductAddToCart
                          product={product}
                          quantity={quantity}
                          loading={isLoading}
                          handleBackInStock={handleBackInStock}
                          isUnavailable={isUnavailable}
                          selectedVariant={activeVariant}
                        />
                      )}
                    </Flex>
                  ) : null}
                </Flex>

                <ProductStock
                  disableNoActiveOption={disableNoActiveOption}
                  handleBackInStock={handleBackInStock}
                  handleQuantity={changeQuantity}
                  noActiveOption={noActiveOption}
                  product={product}
                  siblings={siblings}
                  quantity={quantity}
                  selectedVariant={activeVariant}
                />

                {isHydrated && !isMedium && (
                  <ProductFloatingAddToCart
                    product={product}
                    quantity={quantity}
                    loading={isLoading}
                    handleBackInStock={handleBackInStock}
                    productAddBottomRef={productAddBottomRef}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
          <Box w={{ base: "100%", lg: "50%" }} position="relative" pl={{ base: "0", lg: "7%" }}>
            <ProductMedia images={initialImages} badges={badges} />
            <ScavengerHunt tags={product.tags} layout="pdp" />
          </Box>
        </Flex>
      </Box>

      {/* Product Tabs */}
      <Center background="primary.purpleLight">
        <ProductTabs product={product} reviewsRef={reviewsRef} reviewData={reviewData} />
      </Center>
      <React.Suspense fallback={<div />}>
        <ProductBottom product={product} template={template} reviewData={reviewData} reviewsRef={reviewsRef} />
      </React.Suspense>
      <Box ref={productAddBottomRef}>
        {isHydrated && !isMedium && (
          <ProductAddToCartBottom
            product={product}
            quantity={quantity}
            loading={isLoading}
            handleBackInStock={handleBackInStock}
            isUnavailable={isUnavailable}
            selectedVariant={activeVariant}
            productSizeText={productSizeText}
          />
        )}
      </Box>
    </>
  )
}

export default React.memo(ProductMain)
